import React from 'react';

/**
 * Декоративные примитивы лендинга: силуэты вещей и мини-графики.
 *
 * Всё рисуется инлайновым SVG — на публичной странице нет ни одного бинарного
 * ассета и ни одного внешнего запроса. Графика носит иллюстративный характер,
 * поэтому по умолчанию скрыта от скринридеров (`aria-hidden`), а смысл
 * дублируется текстом рядом.
 */

export type GarmentKind = 'top' | 'bottom' | 'outer' | 'shoes' | 'bag' | 'watch';

const GARMENT_PATHS: Record<GarmentKind, React.ReactNode> = {
    top: <path d="M8.4 3 4.2 5.3 2.6 9.6l2.9 1.1.9-2.1V21h11.2V8.6l.9 2.1 2.9-1.1-1.6-4.3L15.6 3l-1.1 1.5a3.1 3.1 0 0 1-5 0z"/>,
    bottom: <path d="M6.6 3h10.8l1.1 18h-4.7L12 10.6 10.2 21H5.5z"/>,
    outer: (
        <>
            <path d="M9 3 4.4 5.6 2.6 10.2l2.8 1L6.4 9v12h11.2V9l1 2.2 2.8-1-1.8-4.6L15 3l-1.4 2.8a1.8 1.8 0 0 1-3.2 0z"/>
            <path d="M12 6.4v13" strokeWidth="1" className="opacity-40"/>
        </>
    ),
    shoes: <path d="M2.6 12.4h4.2l3.4 2.8h7.2a3.6 3.6 0 0 1 3.6 3.6v1.6H2.6z"/>,
    bag: (
        <>
            <path d="M5 8h14l1.1 12.4H3.9z"/>
            <path d="M9 8.4V6.2a3 3 0 0 1 6 0v2.2" fill="none" stroke="currentColor" strokeWidth="1.6"/>
        </>
    ),
    watch: (
        <>
            <path d="M9.4 2.6h5.2l-.5 4h-4.2z"/>
            <path d="M9.4 21.4h5.2l-.5-4h-4.2z"/>
            <circle cx="12" cy="12" r="5.2"/>
        </>
    ),
};

/** Ненавязчивые «ткани»: тон плитки + тон силуэта, читаемые в обеих темах. */
const GARMENT_TONES: Record<GarmentKind, string> = {
    top: 'bg-sky-50 text-sky-300 dark:bg-sky-500/10 dark:text-sky-400/70',
    bottom: 'bg-slate-100 text-slate-400 dark:bg-white/5 dark:text-slate-400/70',
    outer: 'bg-stone-100 text-stone-400 dark:bg-amber-500/10 dark:text-amber-500/50',
    shoes: 'bg-ink-100 text-ink-300 dark:bg-white/5 dark:text-ink-400/70',
    bag: 'bg-amber-50 text-amber-300 dark:bg-amber-500/10 dark:text-amber-400/60',
    watch: 'bg-brand-50 text-brand-300 dark:bg-brand-500/10 dark:text-brand-400/70',
};

interface GarmentTileProps {
    kind: GarmentKind;
    className?: string;
}

/** Карточка вещи-заглушки: цветная плитка с силуэтом вместо фотографии. */
export const GarmentTile: React.FC<GarmentTileProps> = ({kind, className = ''}) => (
    <div
        className={`flex items-center justify-center rounded-xl overflow-hidden ${GARMENT_TONES[kind]} ${className}`}
        aria-hidden="true"
    >
        <svg viewBox="0 0 24 24" fill="currentColor" className="w-1/2 h-1/2">
            {GARMENT_PATHS[kind]}
        </svg>
    </div>
);

interface RatingDotsProps {
    value: number;
}

/** Пятибалльная оценка вещи в мокапе — точками, как в карточках приложения. */
export const RatingDots: React.FC<RatingDotsProps> = ({value}) => (
    <div className="flex gap-0.5" aria-hidden="true">
        {[1, 2, 3, 4, 5].map(i => (
            <span
                key={i}
                className={`w-1 h-1 rounded-full ${i <= value ? 'bg-brand-500' : 'bg-ink-200 dark:bg-white/15'}`}
            />
        ))}
    </div>
);

export interface DonutSegment {
    id: string;
    label: string;
    percent: number;
}

const DONUT_STROKES = [
    'text-brand-500',
    'text-brand-400',
    'text-brand-300',
    'text-brand-200',
    'text-ink-200 dark:text-white/20',
];

interface DonutChartProps {
    segments: readonly DonutSegment[];
    centerValue: string;
}

/** Кольцевая диаграмма распределения категорий; подписи выводятся легендой рядом. */
export const DonutChart: React.FC<DonutChartProps> = ({segments, centerValue}) => {
    const radius = 42;
    const circumference = 2 * Math.PI * radius;
    let consumed = 0;

    return (
        <div className="relative w-32 h-32 shrink-0" aria-hidden="true">
            <svg viewBox="0 0 100 100" className="w-full h-full -rotate-90">
                <circle cx="50" cy="50" r={radius} fill="none" strokeWidth="12"
                        className="text-ink-100 dark:text-white/5" stroke="currentColor"/>
                {segments.map((segment, index) => {
                    const length = (segment.percent / 100) * circumference;
                    const offset = -(consumed / 100) * circumference;
                    consumed += segment.percent;
                    return (
                        <circle
                            key={segment.id}
                            cx="50" cy="50" r={radius}
                            fill="none" strokeWidth="12" strokeLinecap="butt"
                            stroke="currentColor"
                            className={DONUT_STROKES[index % DONUT_STROKES.length]}
                            strokeDasharray={`${length} ${circumference - length}`}
                            strokeDashoffset={offset}
                        />
                    );
                })}
            </svg>
            <div className="absolute inset-0 flex items-center justify-center">
                <span className="text-2xl font-bold text-ink-900 dark:text-ink-50">{centerValue}</span>
            </div>
        </div>
    );
};

interface DonutLegendProps {
    segments: readonly DonutSegment[];
}

export const DonutLegend: React.FC<DonutLegendProps> = ({segments}) => (
    <ul className="space-y-2 text-sm min-w-0">
        {segments.map((segment, index) => (
            <li key={segment.id} className="flex items-center gap-2">
                <span
                    className={`w-2.5 h-2.5 rounded-sm bg-current shrink-0 ${DONUT_STROKES[index % DONUT_STROKES.length]}`}
                    aria-hidden="true"
                />
                <span className="text-ink-600 dark:text-ink-300 truncate">{segment.label}</span>
                <span className="ml-auto font-semibold text-ink-900 dark:text-ink-50 tabular-nums">
                    {segment.percent}%
                </span>
            </li>
        ))}
    </ul>
);

export interface RadarAxis {
    id: string;
    label: string;
    value: number;
}

interface RadarChartProps {
    axes: readonly RadarAxis[];
}

/** Радар «профиля гардероба»: 5 осей, подписи вынесены в SVG-текст. */
export const RadarChart: React.FC<RadarChartProps> = ({axes}) => {
    const cx = 140;
    const cy = 108;
    const maxRadius = 68;

    const pointAt = (index: number, ratio: number) => {
        const angle = (Math.PI * 2 * index) / axes.length - Math.PI / 2;
        return {
            x: cx + Math.cos(angle) * maxRadius * ratio,
            y: cy + Math.sin(angle) * maxRadius * ratio,
        };
    };

    const ring = (ratio: number) =>
        axes.map((_, i) => {
            const p = pointAt(i, ratio);
            return `${p.x.toFixed(1)},${p.y.toFixed(1)}`;
        }).join(' ');

    const shape = axes.map((axis, i) => {
        const p = pointAt(i, Math.min(1, Math.max(0, axis.value)));
        return `${p.x.toFixed(1)},${p.y.toFixed(1)}`;
    }).join(' ');

    return (
        <svg viewBox="0 0 280 216" className="w-full max-w-[320px]" aria-hidden="true">
            {[0.35, 0.7, 1].map(ratio => (
                <polygon
                    key={ratio}
                    points={ring(ratio)}
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1"
                    className="text-ink-200 dark:text-white/10"
                />
            ))}
            {axes.map((_, i) => {
                const p = pointAt(i, 1);
                return (
                    <line
                        key={axes[i].id}
                        x1={cx} y1={cy} x2={p.x} y2={p.y}
                        stroke="currentColor" strokeWidth="1"
                        className="text-ink-200 dark:text-white/10"
                    />
                );
            })}
            <polygon
                points={shape}
                className="text-brand-500"
                fill="currentColor"
                fillOpacity="0.22"
                stroke="currentColor"
                strokeWidth="2"
            />
            {axes.map((axis, i) => {
                const p = pointAt(i, 1.28);
                const anchor = p.x > cx + 6 ? 'start' : p.x < cx - 6 ? 'end' : 'middle';
                return (
                    <text
                        key={axis.id}
                        x={p.x} y={p.y}
                        textAnchor={anchor}
                        dominantBaseline="middle"
                        className="fill-current text-ink-500 dark:text-ink-400"
                        fontSize="11"
                    >
                        {axis.label}
                    </text>
                );
            })}
        </svg>
    );
};

interface SparkLineProps {
    points: readonly number[];
}

/** Кривая роста числа отмеченных носок; значения нормированы в 0..1. */
export const SparkLine: React.FC<SparkLineProps> = ({points}) => {
    const width = 260;
    const height = 90;
    const step = points.length > 1 ? width / (points.length - 1) : width;
    const coords = points.map((value, i) => `${(i * step).toFixed(1)},${(height - value * height).toFixed(1)}`);
    const line = coords.join(' ');
    const area = `0,${height} ${line} ${width},${height}`;

    return (
        <svg viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="none" className="w-full h-24" aria-hidden="true">
            <polygon points={area} className="text-brand-500" fill="currentColor" fillOpacity="0.12"/>
            <polyline
                points={line}
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="text-brand-500"
                vectorEffect="non-scaling-stroke"
            />
        </svg>
    );
};
